import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

function OptimizedFireflySwarm({ fireflyConfigs }) {
    const meshRef = useRef();
    
    const { geometry, particleData } = useMemo(() => {
        const totalCount = fireflyConfigs.reduce((sum, config) => sum + (config.count || 1), 0);
        
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(totalCount * 3);
        const sizes = new Float32Array(totalCount);
        const speeds = new Float32Array(totalCount);
        const noises = new Float32Array(totalCount * 3);
        const opacities = new Float32Array(totalCount);
        
        const particleData = [];
        let particleIndex = 0;
        
        fireflyConfigs.forEach((config) => {
            const count = config.count || 1;
            const basePos = config.position;
            const scaleArray = Array.isArray(config.scale) ? config.scale : [config.scale, config.scale, config.scale];
            
            for (let i = 0; i < count; i++) {
                // Random offset within the scale volume
                const offsetX = scaleArray[0] * (Math.random() - 0.5);
                const offsetY = scaleArray[1] * (Math.random() - 0.5);
                const offsetZ = scaleArray[2] * (Math.random() - 0.5);
                
                positions[particleIndex * 3] = basePos[0] + offsetX;
                positions[particleIndex * 3 + 1] = basePos[1] + offsetY;
                positions[particleIndex * 3 + 2] = basePos[2] + offsetZ;
                
                // Random size 
                sizes[particleIndex] = Math.random() * 0.3;
                
                // Speed 
                speeds[particleIndex] = 0.8;
                
                // Noise (movement factor) - random direction vector
                noises[particleIndex * 3] = (Math.random() - 0.5) * 2;
                noises[particleIndex * 3 + 1] = (Math.random() - 0.5) * 2;
                noises[particleIndex * 3 + 2] = (Math.random() - 0.5) * 2;
                
                // Opacity
                opacities[particleIndex] = 0.8;
                
                particleData.push({
                    baseX: basePos[0] + offsetX,
                    baseY: basePos[1] + offsetY,
                    baseZ: basePos[2] + offsetZ,
                    noiseX: noises[particleIndex * 3],
                    noiseY: noises[particleIndex * 3 + 1],
                    noiseZ: noises[particleIndex * 3 + 2],
                    speed: speeds[particleIndex]
                });
                
                particleIndex++;
            }
        });
        
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        geometry.setAttribute('speed', new THREE.BufferAttribute(speeds, 1));
        geometry.setAttribute('noise', new THREE.BufferAttribute(noises, 3));
        geometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));
        
        return { geometry, totalCount, particleData };
    }, [fireflyConfigs]);

    useFrame((state) => {
        if (meshRef.current) {
            const time = state.clock.elapsedTime;
            meshRef.current.material.uniforms.time.value = time;
            
            // Sparkles movement pattern
            const positions = meshRef.current.geometry.attributes.position.array;
            particleData.forEach((particle, i) => {
                const t = time * particle.speed * 0.5; 
                
                // Sparkles noise
                positions[i * 3] = particle.baseX + Math.sin(t + particle.noiseX) * particle.noiseX * 0.5;
                positions[i * 3 + 1] = particle.baseY + Math.sin(t + particle.noiseY) * particle.noiseY * 0.5;
                positions[i * 3 + 2] = particle.baseZ + Math.sin(t + particle.noiseZ) * particle.noiseZ * 0.5;
            });
            
            meshRef.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    const shaderMaterial = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                pixelRatio: { value: window.devicePixelRatio }
            },
            vertexShader: `
                uniform float time;
                uniform float pixelRatio;
                attribute float size;
                attribute float speed;
                attribute float opacity;
                
                varying float vOpacity;
                
                void main() {
                    vOpacity = opacity;
                    
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    
                    // Size with subtle pulsing based on time and speed
                    float pulsing = sin(time * speed * 3.0) * 0.1 + 0.9;
                    gl_PointSize = size * 100.0 * pulsing * pixelRatio * (1.0 / -mvPosition.z);
                    
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform float time;
                varying float vOpacity;
                
                void main() {
                    // Create circular particle with soft edges
                    vec2 center = gl_PointCoord - vec2(0.5);
                    float dist = length(center);
                    
                    // Soft circle
                    float alpha = smoothstep(0.5, 0.0, dist);
                    
                    // Twinkle effect (Sparkles signature look)
                    float twinkle = sin(time * 6.0 + gl_PointCoord.x * 50.0) * 0.5 + 0.5;
                    alpha *= mix(0.3, 1.0, twinkle);
                    
                    alpha *= vOpacity;
                    
                    // Warm yellow-white color like Sparkles
                    vec3 color = vec3(1.0, 0.95, 0.7);
                    
                    gl_FragColor = vec4(color, alpha);
                }
            `,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });
    }, []);

    return (
        <points ref={meshRef} geometry={geometry} material={shaderMaterial} />
    );
}

export default OptimizedFireflySwarm;